import React, { useState } from 'react';
import { useAppSelector } from 'hooks';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';

import { AuthTags } from '~constants/authTags';
import { tponeUrl } from '~constants/tpone';

import Icon, { faSignOut, faUser } from '~components/icon';
import Link from '~components/Link';

import { selectAuthenticatedUser, selectLinkedAccounts, selectUserTags } from '~main/selectors/login';

/**
 * UserMenu component displays a user dropdown menu for user details, account switching, and logout.
 *
 * @component
 * @returns The rendered UserMenu component.
 */
const UserMenu: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = useAppSelector(selectAuthenticatedUser);
  const linkedAccounts = useAppSelector(selectLinkedAccounts);
  const tags = useAppSelector(selectUserTags);

  const userFullName = user ? `${user.first_name} ${user.surname}` : '';
  const REDIRECT_URL = tponeUrl;

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const canAccountSwitch = (linkedAccounts?.length ?? 0) > 1;
  const canUpdateCompany = tags.includes(AuthTags.COMPANY.API.UPDATE);

  return (
    <div className="d-flex flex-row align-items-center menu-user p-2">
      <div className="d-flex me-3">
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <DropdownToggle nav caret className="menu-link p-1">
            <>
              <Icon icon={faUser} />
              <span className="ms-2">{userFullName}</span>
            </>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem text className="small fw-bold">
              Name
            </DropdownItem>
            <DropdownItem text className="small ps-4">
              {userFullName}
            </DropdownItem>
            <DropdownItem text className="small fw-bold">
              Company
            </DropdownItem>
            <DropdownItem text className="small ps-4">
              {user?.company.name ?? ''}
            </DropdownItem>
            <DropdownItem divider />
            {canUpdateCompany && (
              <>
                <DropdownItem header>Company Config</DropdownItem>
                <Link to="/company/access-tokens" className="menu-logout">
                  <DropdownItem>Access Tokens</DropdownItem>
                </Link>
                <DropdownItem divider />
              </>
            )}
            {canAccountSwitch && (
              <>
                <DropdownItem header>Switch Account</DropdownItem>
                {linkedAccounts?.map(({ value, label }, index) => (
                  <DropdownItem
                    key={index}
                    onClick={() =>
                      window.location.replace(`${REDIRECT_URL}/user-linked-account-swap.asp?swapID=${value}`)
                    }
                  >
                    <span className="">{label}</span>
                  </DropdownItem>
                ))}
                <DropdownItem divider />
              </>
            )}
            <Link className="menu-logout" to="/logout">
              <DropdownItem>
                <div className="d-flex justify-content-between w-100 align-items-center">
                  <span>Log out </span>
                  <Icon icon={faSignOut} />
                </div>
              </DropdownItem>
            </Link>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};

export default UserMenu;
